import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { batchCount } from "../store/actions/generalActions";
import { getUserRole, hasValidTokens } from "../utils/authUtils";
import { Icon } from "@fluentui/react";

import portalIcon from "../images/portal-icon.svg";
import userIcon from "../images/user-icon.png";
import bellIcon from "../images/bell-icon.png";

const Sidebar = ({ isOpen, onClose, onCollapsedChange }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loading, data: portals } = useSelector(state => state.api.portals);

    const hasRedirected = useRef(false);
    const hasLoadedOnce = useRef(false);

    const [badges, setBadges] = useState({});
    const [isCollapsed, setIsCollapsed] = useState(false);
    const userRole = getUserRole();

    useEffect(() => {
        if (loading) {
            hasLoadedOnce.current = true;
        }
    }, [loading]);

    useEffect(() => {
        if (!hasRedirected.current && hasLoadedOnce.current && !loading && portals !== undefined && portals !== null) {
            if (portals?.length > 0) {
                navigate(`/portal/${portals[0].id}`, {
                    state: { portal: portals[0] }
                });
                hasRedirected.current = true;
            } else if (portals.length === 0) {

                navigate('/general-manage');
                hasRedirected.current = true;
            }
        }
    }, [portals, navigate, loading]);

    // Fetch batch counts every 20 seconds
    useEffect(() => {
        // Only start interval if we have valid tokens and portals
        if (!hasValidTokens()) {
            return;
        }

        const fetchBatchCounts = async () => {
            try {
                const dataSourceIds = portals?.flatMap(portal =>
                    portal.dataSources?.map(ds => ds.id) || []
                ) || [];

                const result = await dispatch(batchCount('/workitems/count/batch', {
                    portalDataSourceIds: dataSourceIds,
                    status: 1
                }));

                const newBadges = {};

                if (result) {
                    portals.forEach(portal => {
                        let totalCurrentCount = 0;

                        portal.dataSources?.forEach(ds => {
                            const count = result?.counts?.[ds.id] || result?.[ds.id] || 0;
                            totalCurrentCount += count;
                        });

                        if (totalCurrentCount > 0) {
                            newBadges[portal.id] = true;
                        }
                    });

                    // Check for general items (id: -1)
                    const generalCount = result?.['-1'] || result?.["-1"] || 0;
                    if (generalCount > 0) {
                        newBadges['general'] = true;
                    }

                    // Check for notification items (id: -2)
                    const notificationCount = result?.['-2'] || result?.["-2"] || 0;
                    if (notificationCount > 0) {
                        newBadges['notifications'] = true;
                    }
                }

                setBadges(newBadges);
            } catch (error) { }
        };

        // Set up interval for every 20 seconds (first call happens after 20 seconds)
        const interval = setInterval(fetchBatchCounts, 20000);

        // Cleanup interval on unmount
        return () => clearInterval(interval);
    }, [portals, dispatch]);

    return (
        <div className={`sidebar ${isOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''}`} >

            <div className="mob-sidebar-header">
                <div> Next Up</div>
                <button className="close-btn" onClick={onClose}>&times;</button>
            </div>

            <div className="sidebar-toggle" onClick={() => {
                const newCollapsed = !isCollapsed;
                setIsCollapsed(newCollapsed);
                if (onCollapsedChange) onCollapsedChange(newCollapsed);
            }}>
                <Icon
                    iconName={isCollapsed ? "DoubleChevronRight" : "DoubleChevronLeft"}
                    className="toggle-icon"
                />
            </div>

            {/* <NavLink to="/dashboard" className="nav-item">Dashboard</NavLink> */}

            {loading && !isCollapsed && <p style={{ paddingLeft: "15px" }}>Loading portals...</p>}


            {portals.map((portal) => (
                <NavLink
                    key={portal.id}
                    to={`/portal/${portal.id}`}
                    state={{ portal }}
                    className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                    title={isCollapsed ? portal.name : ""}
                >
                    <img
                        className="portal-icon"
                        src={portal.externalPortalIconFileUrl || portalIcon}
                        alt={`${portal.name} icon`}
                    />
                    {!isCollapsed && (
                        <span className="text-ellipsis">
                            {portal.name}
                        </span>
                    )}
                    {badges[portal.id] && <span className="active-indicator"></span>}
                </NavLink>
            ))}

            <NavLink to="/general-manage" className="nav-item" title={isCollapsed ? "General" : ""}>
                <img className="portal-icon" src={portalIcon} alt="upload-image" />
                {!isCollapsed && <span className="text-ellipsis">General</span>}
                {badges['general'] && <span className="active-indicator"></span>}
            </NavLink>

            <NavLink to="/notifications" className="nav-item" title={isCollapsed ? "Notifications" : ""}>
                <img className="portal-icon" src={bellIcon} alt="notifications" />
                {!isCollapsed && <span>Notifications</span>}
                {badges['notifications'] && <span className="active-indicator"></span>}
            </NavLink>

            {userRole === 'Admin' && (
                <NavLink to="/user-manage" className="nav-item" title={isCollapsed ? "Users" : ""}>
                    <img className="portal-icon" src={userIcon} alt="upload-image" />
                    {!isCollapsed && <span>Users</span>}
                </NavLink>
            )}

        </div>
    );
};

export default Sidebar;
